/* eslint-disable import/prefer-default-export */

import { isWithinInterval } from "date-fns";

import {
  getSRAssetDescription,
  getSRAssetTitle,
  getSRStream,
  getSRThumbnail,
} from "./partners/sportradar";
import { getVCAssetTitle, getVCStream } from "./partners/videociety";
import { PARTNER_IDENTIFIER } from "./partners";
import { sportradarApi, videocietyApi } from "./apis";

export const getCurrentDate = () => new Date();

export const isGoingToPlay = (video, clockTime = getCurrentDate()) => {
  if (!video) return false;
  const start = new Date(video.start);
  const end = new Date(video.end);

  return isWithinInterval(clockTime, { start, end });
};

export const isCurrentlyPlaying = (video) =>
  isGoingToPlay(video, getCurrentDate());

export const appendObjectToAsset = (asset, object) =>
  Object.assign({}, asset, object);

export const standardizeStreamObject = (source, mimeType) => ({
  stream: { source, mimeType },
});

export function getAsset(partnerIdentifier, assetID) {
  switch (partnerIdentifier) {
    case PARTNER_IDENTIFIER.VIDEOCIETY:
      return videocietyApi
        .getItems(assetID)
        .then((items) => items?.length > 0 && items[0])
        .then((item) => getStreamableAsset(partnerIdentifier, item));
    case PARTNER_IDENTIFIER.RLAXX:
    default:
      return sportradarApi
        .getAsset(assetID)
        .then((asset) => getStreamableAsset(partnerIdentifier, asset));
  }
}

export function getAssetStream(asset) {
  if (!asset) return undefined;
  return asset.stream;
}

export function getAssetTitle(asset) {
  if (!asset) return undefined;
  switch (asset.partner) {
    case PARTNER_IDENTIFIER.VIDEOCIETY:
      return getVCAssetTitle(asset);
    case PARTNER_IDENTIFIER.RLAXX:
    default:
      return getSRAssetTitle(asset);
  }
}

export function getAssetDescription(asset) {
  if (!asset) return undefined;
  switch (asset.partner) {
    case PARTNER_IDENTIFIER.VIDEOCIETY:
      return asset?.synopsis;
    case PARTNER_IDENTIFIER.RLAXX:
    default:
      return getSRAssetDescription(asset);
  }
}

export function getAssetLanguages(asset) {
  if (!asset) return undefined;
  switch (asset.partner) {
    case PARTNER_IDENTIFIER.VIDEOCIETY:
      return asset?.languages;
    case PARTNER_IDENTIFIER.RLAXX:
    default:
      return undefined;
  }
}

export function getAssetCoverImage(asset, manipulation = "400x225") {
  if (!asset) return undefined;
  switch (asset.partner) {
    case PARTNER_IDENTIFIER.VIDEOCIETY:
      return asset.images?.cover;
    case PARTNER_IDENTIFIER.RLAXX:
    default:
      return (
        asset?.thumbnail || asset?.image || getSRThumbnail(asset, manipulation)
      );
  }
}

export function getAssetBackgroundImage(asset, manipulation = "400x225") {
  if (!asset) return undefined;
  switch (asset.partner) {
    case PARTNER_IDENTIFIER.VIDEOCIETY:
      return asset?.images?.screen;
    case PARTNER_IDENTIFIER.RLAXX:
    default:
      return (
        asset?.thumbnail || asset?.image || getSRThumbnail(asset, manipulation)
      );
  }
}

export function getAssetGenres(asset) {
  if (!asset) return undefined;
  switch (asset.partner) {
    case PARTNER_IDENTIFIER.VIDEOCIETY:
      return asset.genres.map((genre) => genre.name);
    case PARTNER_IDENTIFIER.RLAXX:
    default:
      // todo check how to get asset genres
      return undefined;
  }
}

export function getAssetYear(asset) {
  if (!asset) return undefined;
  switch (asset.partner) {
    case PARTNER_IDENTIFIER.VIDEOCIETY:
      return asset.year;
    case PARTNER_IDENTIFIER.RLAXX:
    default:
      return undefined;
  }
}

export function getAssetDuration(asset) {
  if (!asset) return undefined;
  // in minutes
  switch (asset.partner) {
    case PARTNER_IDENTIFIER.VIDEOCIETY:
      return asset.duration;
    case PARTNER_IDENTIFIER.RLAXX:
    default:
      return Math.round(asset.playtime / 60);
  }
}

export function getAssetMinAge(asset) {
  if (!asset) return undefined;
  switch (asset.partner) {
    case PARTNER_IDENTIFIER.VIDEOCIETY:
      return asset.minAge;
    case PARTNER_IDENTIFIER.RLAXX:
    default:
  }
}

export function getAssetSomethingSample(asset) {
  if (!asset) return undefined;
  switch (asset.partner) {
    case PARTNER_IDENTIFIER.VIDEOCIETY:
      break;
    case PARTNER_IDENTIFIER.RLAXX:
    default:
      break;
  }
}

export function getStreamableAsset(partnerIdentifier, asset) {
  switch (partnerIdentifier) {
    case PARTNER_IDENTIFIER.VIDEOCIETY:
      return getVCStream(asset);
    case PARTNER_IDENTIFIER.RLAXX:
    default:
      return getSRStream(asset);
  }
}

export function liveVideoTotalProgress(startUnix) {
  const time = getCurrentDate();

  const current = time.getTime() / 1000;
  return current - startUnix;
}

/**
 *
 * @param {String} text
 * @param {Number} originalFontSize
 * @returns {String} className
 */
export const getTitleSizeClassname = (text, originalFontSize) => {
  if (!text) return undefined;
  const textLength = parseInt(text.length, 10);
  let className = "title--size-xxl";
  if (textLength >= 57) {
    className = "title--size-s";
  } else if (textLength >= 52) {
    className = "title--size-m";
  } else if (textLength >= 47) {
    className = "title--size-l";
  } else if (textLength >= 35) {
    className = "title--size-xl";
  }
  return className;
};

/**
 *
 * @param timeInSeconds
 * @returns {string}
 */
export const parseTime = (timeInSeconds) => {
  if (!timeInSeconds) return "00:00";
  const hours = Math.floor(timeInSeconds / (60 * 60));
  const minutes = Math.floor((timeInSeconds % (60 * 60)) / 60);
  const seconds = Math.floor(timeInSeconds) % 60;
  if (isNaN(seconds)) return "00:00";

  let parsed = [minutes, seconds].map(pad2);
  if (hours) parsed = [hours, ...parsed];
  return parsed.join(":");
};

const pad2 = (number) => (number < 10 ? "0" : "") + number;

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  isCurrentlyPlaying,
  getStreamableAsset,
  liveVideoTotalProgress,
  getTitleSizeClassname,
  parseTime,
};
